import { useParams, useNavigate } from "react-router-dom";
import { useWorkItem } from "@/modules/queue/api/useWorkItem";
import { ContextSection } from "./ContextSection";
import { ActivityLog } from "./ActivityLog";
import { WorkItemActions } from "./WorkItemActions";
import { Badge } from "@hub/ui/components/ui/badge";
import { Button } from "@hub/ui/components/ui/button";
import { Skeleton } from "@hub/ui/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";
import { statusColors } from "@/core/constants/statusColors";

export function WorkItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { item, loading } = useWorkItem(id);

  if (loading) {
    return (
      <div className="p-8 space-y-6 max-w-4xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <p className="text-gray-400">Work item not found</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/queue")}
        className="text-gray-500"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Queue
      </Button>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {item.sourceRef || item.id.slice(0, 12)}
          </h1>
          <Badge className={`text-sm ${statusColors[item.status] ?? statusColors.open}`}>
            {item.status.replace(/_/g, " ")}
          </Badge>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {item.type} &middot; {item.claimedBy ? `Claimed by ${item.claimedBy}` : "Unclaimed"}
          &middot; ID: {item.id}
        </p>
      </div>

      <div className="bg-white dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 p-6">
        <ContextSection item={item} />
      </div>

      <div className="bg-white dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 p-6">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Actions
        </h3>
        <WorkItemActions item={item} />
      </div>

      <div className="bg-white dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 p-6">
        <ActivityLog itemId={item.id} />
      </div>
    </div>
  );
}
